import Moment from 'moment';
import React, { useEffect } from 'react';
import ReactDatePicker from 'react-datepicker';
import { IQuestion, QuestionType } from 'src/entities/question';
import Checkbox from '../Checkbox/Checkbox';
import TextInput, { TextInputType } from '../TextInput/TextInput';
import { useIntl } from 'react-intl';
import { registerDateFnsLocale } from '../../BookingFlow/modules/helpers';
import Select from '../Select/Select';
import Label from '../Label/Label';
import 'moment/locale/hu';
import 'moment/locale/de';
import 'moment/locale/sk';
import 'moment/locale/en-gb';
import DateInput from '../DateInput/DateInput';

interface IFormWidgetProps {
    question: IQuestion;
    onChange: (answer: string) => void;
    answer: string;
}

const FormWidget: React.FC<IFormWidgetProps> = ({ question, onChange, answer }) => {
    const intl = useIntl();
    const id = `question-${question.Id}-${Date.now()}`;
    const handleInputChange = (value: string | boolean) => {
        onChange(String(value));
    };
    const handleDateChange = (event: React.FocusEvent<HTMLInputElement>) => {
        const parsedDate = Moment(event.target.value, [
            'LL',
            'YYYY-MM-DD',
            'DD-MM-YYYY',
            'MM-DD-YYYY',
        ]);
        onChange(parsedDate.isValid() ? parsedDate.toISOString() : '');
    };

    useEffect(() => {
        registerDateFnsLocale(intl.locale);
        Moment.locale(intl.locale === 'en' ? 'en-gb' : intl.locale);
    }, [intl.locale]);

    return (
        <>
            {question.Type === QuestionType.YesNo ? null : (
                <Label
                    className="question__name"
                    message={question.Name}
                    required={question.Required}
                    field={id}
                />
            )}
            {question.Type === QuestionType.FreeText && (
                <TextInput
                    fieldName={question.FieldName}
                    required={question.Required}
                    onChange={handleInputChange}
                    type={TextInputType.Text}
                    value={answer || ''}
                    isMultiLine={true}
                    id={id}
                />
            )}
            {question.Type === QuestionType.Date && (
                <ReactDatePicker
                    popperPlacement="top-start"
                    popperModifiers={[
                        {
                            name: 'preventOverflow',
                            enabled: true,
                            options: {
                                rootBoundary: 'viewport',
                            },
                        },
                    ]}
                    locale={intl.locale === 'en' ? 'en-GB' : intl.locale}
                    dateFormat={'PPP'}
                    customInput={<DateInput />}
                    shouldCloseOnSelect={true}
                    minDate={Moment().add(question.MinimumValue, 'years').toDate()}
                    maxDate={Moment().add(question.MaximumValue, 'years').toDate()}
                    onChange={() => undefined}
                    onSelect={(date) => onChange(date?.toISOString() || '')}
                    onBlur={handleDateChange}
                    selected={answer ? new Date(answer) : undefined}
                    showMonthDropdown={true}
                    showYearDropdown={true}
                    dropdownMode="select"
                    calendarClassName="react-datepicker--without-header"
                    id={id}
                />
            )}
            {question.Type === QuestionType.List && (
                <Select
                    values={question.Values.map((actValue) => {
                        return { value: actValue.Value, label: actValue.Text };
                    })}
                    value={answer}
                    onChange={onChange}
                    id={id}
                />
            )}
            {question.Type === QuestionType.YesNo && (
                <Checkbox
                    fieldName={question.FieldName}
                    onChange={handleInputChange}
                    required={question.Required}
                    checked={answer.toLowerCase() === 'true'}
                >
                    {question.Name}
                </Checkbox>
            )}
        </>
    );
};

export default FormWidget;
